import { useState } from "react";
import { useNavigate } from "@/lib/router-compat";
import { Button } from "@/components/ui/button";
import { Trophy, GraduationCap, Award } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { learningService } from "@/services/learningService";
import { useAuth } from "@/context/AuthContext";
import { LearningPathSelectionCard } from "@/components/learning/LearningPathSelectionCard";
import { LearningPathSkeleton } from "@/components/ui/skeletons";
import AchievementsModal from "@/components/learning/AchievementsModal";
import { PageHeader } from "@/components/ui/page-header";
import { PageShell } from "@/components/layout/PageShell";
import { EmptyState } from "@/components/ui/empty-state";
import { useT } from "@/i18n/useLocale";
import T from "@/i18n/T";



const Learn = () => {
  const t = useT();
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const [isAchievementsOpen, setIsAchievementsOpen] = useState(false);
  
  const { data: learningPaths = [], isLoading: pathsLoading } = useQuery({
    queryKey: ['learningPaths'],
    queryFn: () => learningService.getLearningPaths(),
  });

  const { data: userProgress = [] } = useQuery({
    queryKey: ['userProgress', user?.id],
    queryFn: () => learningService.getUserProgress(user?.id || ''),
    enabled: isAuthenticated,
  });

  const { data: allAchievements = [] } = useQuery({
    queryKey: ['learningAchievements'],
    queryFn: () => learningService.getLearningAchievements(),
  });

  const { data: userAchievements = [] } = useQuery({
    queryKey: ['userLearningAchievements', user?.id],
    queryFn: () => learningService.getUserLearningAchievements(user?.id || ''),
    enabled: isAuthenticated,
  });

  const handlePathSelect = (pathId: string) => {
    navigate(`/learn/${pathId}`);
  };
  
  return (
    <PageShell>
      <PageHeader
        eyebrow={t("auto.learn-2f1a")}
        title={t("auto.web3-learning-2978")}
        description={t("auto.choose-a-path-to-start-your-journey-into-527b")}
        actions={
          <>
            <Button onClick={() => navigate("/leaderboard")} variant="outline" size="sm" className="gap-2">
              <Trophy className="w-4 h-4" />
              <T k="auto.leaderboard-e02b" />
            </Button>
            <Button onClick={() => setIsAchievementsOpen(true)} variant="outline" size="sm" className="gap-2">
              <Award className="w-4 h-4" />
              <T k="auto.achievements-f5c7" />
            </Button>
          </>
        }
      />

      {pathsLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {[...Array(8)].map((_, i) => (
            <LearningPathSkeleton key={i} />
          ))}
        </div>
      ) : learningPaths.length === 0 ? (
        <EmptyState icon={GraduationCap} title={t("auto.no-learning-paths-yet-8f21")} description={t("auto.new-courses-are-on-the-way-2f85")} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {learningPaths.map((path) => (
            <LearningPathSelectionCard
              key={path.id}
              path={path}
              learnerCount="10K+ learners"
              onSelect={handlePathSelect}
            />
          ))}
        </div>
      )}

      <AchievementsModal
        isOpen={isAchievementsOpen}
        onClose={() => setIsAchievementsOpen(false)}
        allAchievements={allAchievements}
        userAchievements={userAchievements}
      />
    </PageShell>

  );
};

export default Learn;
